import { faArrowDown, faArrowUp } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { theme } from '@orobix/orobix-fe-components'

import { hasKpiValue } from '../../../utils/kpi'

type DeltaCellProps = {
  /** Signed absolute delta (already an integer from backend). */
  abs: number | null
  /** Signed percentage delta (already ×100 from backend). */
  pct: number | null
  /** Which sign counts as an improvement (green). */
  goodWhen: 'positive' | 'negative'
  /**
   * Optional before/after share (already ×100). When both are provided, the
   * parenthetical shows `before% → after%` instead of the single delta pct.
   */
  beforePct?: number | null
  afterPct?: number | null
}

// Absolutes are already integers: show with their natural sign (e.g. -7, 12).
const absFormatter = new Intl.NumberFormat('it-IT', {
  maximumFractionDigits: 0,
})

// Percentages are already ×100: just append %, round in display, force the sign.
const pctFormatter = new Intl.NumberFormat('it-IT', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  signDisplay: 'exceptZero',
})

// Shares are absolute percentages (already ×100): no forced sign.
const shareFormatter = new Intl.NumberFormat('it-IT', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/**
 * Renders a `from_last_config` delta KPI as `abs (pct%)` with an up/down arrow
 * and green/red coloring. Improvement (green) depends on `goodWhen`: automation
 * improves when positive, refill improves when negative. When `beforePct` and
 * `afterPct` are given, the parenthetical becomes `before% → after%` instead.
 */
export const DeltaCell = ({
  abs,
  pct,
  goodWhen,
  beforePct,
  afterPct,
}: DeltaCellProps) => {
  if (!hasKpiValue(abs)) {
    return <div style={{ textAlign: 'right' }}>N/A</div>
  }

  const isImprovement = goodWhen === 'positive' ? abs > 0 : abs < 0

  let color = theme.gray03
  if (abs !== 0) color = isImprovement ? theme.green01 : theme.red01

  let arrow = null
  if (abs > 0) arrow = faArrowUp
  else if (abs < 0) arrow = faArrowDown

  let pctText = ''
  if (hasKpiValue(beforePct) && hasKpiValue(afterPct)) {
    pctText = ` (${shareFormatter.format(beforePct)}% → ${shareFormatter.format(
      afterPct
    )}%)`
  } else if (hasKpiValue(pct)) {
    pctText = ` (${pctFormatter.format(pct)}%)`
  }

  return (
    <div style={{ textAlign: 'right', color, whiteSpace: 'nowrap' }}>
      {arrow && (
        <FontAwesomeIcon icon={arrow} style={{ marginRight: '0.25rem' }} />
      )}
      {absFormatter.format(abs)}
      {pctText}
    </div>
  )
}
